import { type CustomerDocument } from '@utazzo/shared';
import { UzBadge, UzButton, UzConfirmDialog, UzStatusBadge } from '@utazzo/ui';
import { Download, FileText, Plus, Trash2 } from 'lucide-react';
import React, { useState } from 'react';

import { useDeleteDocument, useDownloadDocument } from '../use-customers';

import { DocumentUploadDialog } from './DocumentUploadDialog';

interface DocumentsStepProps {
  customerId: string;
  documents: CustomerDocument[];
}

const kindLabel: Record<string, string> = {
  PASSPORT: 'Passport',
  VISA: 'Visa',
  PAN: 'PAN card',
  AADHAAR: 'Aadhaar',
  GST_CERTIFICATE: 'GST certificate',
  COMPANY_REGISTRATION: 'Company registration',
  OTHER_ID: 'Other ID',
  INVOICE: 'Invoice',
  OTHER: 'Other',
};

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

/**
 * Step 3 of the customer stepper.
 *
 * Lists uploaded documents with their kind, title, document number, and
 * expiry. Each row has:
 *  - Download: GETs a presigned URL then opens it in a new tab.
 *  - Delete: soft-deletes the row (the S3 object is cleaned up by the
 *    retention reaper in Phase 8).
 *
 * New documents are added via DocumentUploadDialog (two-step S3 flow).
 */
export const DocumentsStep = ({ customerId, documents }: DocumentsStepProps): React.JSX.Element => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<CustomerDocument | null>(null);
  const deleteDocument = useDeleteDocument();
  const downloadDocument = useDownloadDocument();

  const handleDownload = async (doc: CustomerDocument) => {
    const result = await downloadDocument.mutateAsync({
      customerId,
      documentId: doc.id,
    });
    window.open(result.presignedUrl, '_self', 'noopener,noreferrer');
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-600">
          {documents.length === 0
            ? 'No documents yet. Upload one below.'
            : `${documents.length} document${documents.length === 1 ? '' : 's'}`}
        </p>
        <UzButton size="sm" onClick={() => setDialogOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          Upload document
        </UzButton>
      </div>

      {documents.length > 0 && (
        <ul className="space-y-3">
          {documents.map((doc) => (
            <li
              key={doc.id}
              className="bg-background rounded-lg border p-4"
              aria-label={`Document: ${doc.title}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <FileText className="mt-0.5 h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
                  <div className="min-w-0 space-y-0.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <UzBadge tone="secondary">
                        {kindLabel[doc.documentKind] ?? doc.documentKind}
                      </UzBadge>
                      {doc.isPrimary && <UzStatusBadge status="active" />}
                    </div>
                    <p className="truncate text-sm font-medium text-zinc-900">{doc.title}</p>
                    {doc.documentNumber && (
                      <p className="text-xs text-zinc-500">No. {doc.documentNumber}</p>
                    )}
                    <div className="flex flex-wrap gap-3 text-xs text-zinc-400">
                      {doc.issuedCountry && <span>{doc.issuedCountry.name}</span>}
                      {doc.issueDate && <span>Issued {doc.issueDate}</span>}
                      {doc.expiryDate && <span>Expires {doc.expiryDate}</span>}
                      <span>{formatBytes(doc.sizeBytes)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <UzButton
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDownload(doc)}
                    loading={downloadDocument.isPending}
                    aria-label={`Download ${doc.title}`}
                  >
                    <Download className="h-4 w-4 text-zinc-400" />
                  </UzButton>
                  <UzButton
                    size="sm"
                    variant="ghost"
                    onClick={() => setDeleteTarget(doc)}
                    aria-label={`Delete ${doc.title}`}
                  >
                    <Trash2 className="h-4 w-4 text-zinc-400" />
                  </UzButton>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <DocumentUploadDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        customerId={customerId}
      />

      <UzConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title="Delete this document?"
        description={
          deleteTarget
            ? `"${deleteTarget.title}" will be removed. The file in storage will be cleaned up automatically.`
            : ''
        }
        confirmLabel="Delete"
        tone="destructive"
        onConfirm={async () => {
          if (deleteTarget) {
            await deleteDocument.mutateAsync({ customerId, documentId: deleteTarget.id });
            setDeleteTarget(null);
          }
        }}
      />
    </div>
  );
};
